import { AfterViewInit, computed, ContentChildren, Directive, input, OnDestroy, QueryList, signal } from '@angular/core';
import { Subscription } from 'rxjs';
import { ElementScrollerDirective } from '../component-scroller/component-scroller.directive';

@Directive({
  selector: '[elementScrollerMediator]',
  standalone: true,
  exportAs: 'elementScrollerMediator'
})
/**
 * @description
 * When placed on a parent HTML element, this directive acts as a mediator
 * that discovers and provides access to descendant {@link ElementScrollerDirective} instances.
 * It queries all nested `elementScroller` directives via `ContentChildren` and exposes
 * the matching one (by `elementScrollerId`) through a computed signal.
 *
 * @example
 * <div elementScrollerMediator [elementScrollerId]="'my-scroller'" #mediator="elementScrollerMediator">
 *   <div elementScroller [elementScrollerId]="'my-scroller'">...</div>
 * </div>
 *
 * <!-- trigger scroll from outside -->
 * <button (click)="mediator.elementScroller()?.scrollToElementBottom()">Scroll</button>
 */
export class ElementScrollerMediatorDirective implements AfterViewInit, OnDestroy {
  /**
   * @description
   * Unique identifier used to match the corresponding {@link ElementScrollerDirective} among descendants.
   */
  public elementScrollerId = input.required<string>();

  @ContentChildren(ElementScrollerDirective, { descendants: true })
  private elementScrollers!: QueryList<ElementScrollerDirective>;

  private _elementScrollersSignal = signal<ElementScrollerDirective[] | null>(null);
  private _subscriptions = new Subscription();

  /**
   * @description
   * Computed signal that resolves to the descendant {@link ElementScrollerDirective}
   * whose `elementScrollerId` matches this mediator's `elementScrollerId`.
   * Returns `undefined` if no matching scroller is found.
   */
  elementScroller = computed(() => {
    return this._elementScrollersSignal()?.find(elementScroller => elementScroller.elementScrollerId() === this.elementScrollerId());
  });

  ngAfterViewInit(): void {
    const elementScrollersChangeSubscription = this.elementScrollers.changes.subscribe(() => {
      this.setelementScrollersSignal();
    });
    this._subscriptions.add(elementScrollersChangeSubscription);

    this.setelementScrollersSignal();
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  private setelementScrollersSignal() {
    this._elementScrollersSignal.set(this.elementScrollers.toArray());
  }
}
